import {Button, CrossIcon, Input, ModalPopup} from '@kit';

import React, {useCallback, useState} from 'react';
import {useApolloClient} from '@apollo/client/react';

import {
	useDeleteEntityImageMutation,
	useReorderEntityImagesMutation,
	useUpdateImageMutation,
} from '../../generated/types';
import {useDeleteConfirm} from '../use-delete-confirm';
import type {GalleryImage} from './gallery-context';
import type {GalleryTarget} from './gallery-upload';

import css from './gallery.module.css';

/**
 * Действия администратора над картинками сущности: порядок, подпись и автор, удаление.
 * Общие для миниатюр галереи и главной картинки; окна правки и подтверждения возвращаются в dialogs.
 */
export function useGalleryAdmin(target: GalleryTarget, allImages: GalleryImage[]) {
	const client = useApolloClient();
	const [reorder] = useReorderEntityImagesMutation();
	const [update, {loading: saving}] = useUpdateImageMutation();
	const [remove] = useDeleteEntityImageMutation();

	const [editing, setEditing] = useState<GalleryImage>();
	const [credits, setCredits] = useState('');
	const [description, setDescription] = useState('');
	const [error, setError] = useState<string>();

	const refresh = useCallback(() => client.refetchQueries({include: 'active'}), [client]);

	const move = useCallback(
		async (id: string, to: number) => {
			const ids = allImages.map((image) => image.id);
			const from = ids.indexOf(id);
			if (from < 0 || to < 0 || to >= ids.length || from === to) return;
			ids.splice(to, 0, ids.splice(from, 1)[0]);
			setError(undefined);
			const result = await reorder({variables: {target: target.type, targetId: target.id, imageIds: ids}});
			if (result.error) setError(result.error.message);
			await refresh();
		},
		[allImages, reorder, target, refresh],
	);

	const shift = useCallback(
		(id: string, step: -1 | 1) =>
			move(
				id,
				allImages.findIndex((image) => image.id === id) + step,
			),
		[allImages, move],
	);

	const handleDelete = useCallback(
		async (imageId: string) => {
			setError(undefined);
			const result = await remove({variables: {target: target.type, targetId: target.id, imageId}});
			if (result.error) setError(result.error.message);
			const removedPath = result.data?.deleteEntityImage;
			if (removedPath) {
				const response = await fetch(`/api/gallery-image?path=${encodeURIComponent(removedPath)}`, {method: 'DELETE'});
				if (!response.ok) setError('Картинка убрана, но файлы удалить не удалось');
			}
			await refresh();
		},
		[remove, target, refresh],
	);

	const {openConfirm, DeleteConfirm} = useDeleteConfirm({
		callback: handleDelete,
		title: 'Убрать картинку',
		text: 'Картинка пропадёт из галереи. Если она больше нигде не используется, будут удалены и её файлы.',
	});

	const edit = useCallback((image: GalleryImage) => {
		setEditing(image);
		setCredits(image.credits || '');
		setDescription(image.description || '');
		setError(undefined);
	}, []);

	const save = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!editing) return;
		const result = await update({variables: {id: editing.id, credits, description}});
		if (result.error) {
			setError(result.error.message);
			return;
		}
		setEditing(undefined);
		await refresh();
	};

	const dialogs = (
		<>
			{DeleteConfirm}
			<ModalPopup open={!!editing} onClose={() => setEditing(undefined)} header="Изменить картинку" maxWidth={520}>
				{editing && (
					<form className={css.uploadForm} onSubmit={save}>
						<img className={css.editPreview} src={editing.preview} alt="" />
						<Input label="Автор" value={credits} onChange={setCredits} />
						<Input label="Подпись" value={description} onChange={setDescription} />
						{error && <div className={css.uploadError}>{error}</div>}
						<div className={css.uploadButtons}>
							<Button type="submit" appearance="primary" loading={saving}>
								Сохранить
							</Button>
							<Button onClick={() => setEditing(undefined)} icon={<CrossIcon />}>
								Отмена
							</Button>
						</div>
					</form>
				)}
			</ModalPopup>
		</>
	);

	return {error, move, shift, edit, requestDelete: openConfirm as (id: string) => void, dialogs};
}
